import { useCallback, useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Toaster } from "sonner";
import "@/App.css";
import { CursorGlow } from "@/components/portfolio/CursorGlow";
import { Navbar } from "@/components/portfolio/Navbar";
import { Hero } from "@/components/portfolio/Hero";
import { Marquee } from "@/components/portfolio/Marquee";
import { About } from "@/components/portfolio/About";
import { Stack } from "@/components/portfolio/Stack";
import { Services } from "@/components/portfolio/Services";
import { Process } from "@/components/portfolio/Process";
import { Portfolio } from "@/components/portfolio/Portfolio";
import { Plans } from "@/components/portfolio/Plans";
import { Testimonials } from "@/components/portfolio/Testimonials";
import { Contact } from "@/components/portfolio/Contact";
import { Footer } from "@/components/portfolio/Footer";
import { ChatWidget } from "@/components/portfolio/ChatWidget";
import { Preloader, shouldPreload } from "@/components/portfolio/Preloader";
import { ErrorBoundary } from "@/components/portfolio/ErrorBoundary";
import { API } from "@/lib/api";
import { scrollToSection } from "@/lib/scroll";
import AdminPage from "@/pages/AdminPage";
import AuthPage from "@/pages/AuthPage";
import AccountPage from "@/pages/AccountPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import NotFoundPage from "@/pages/NotFoundPage";

const Home = ({ anchor }) => {
  const [preloading, setPreloading] = useState(shouldPreload);
  const [ready, setReady] = useState(!preloading);
  const onReady = useCallback(() => setReady(true), []);
  const onDone = useCallback(() => setPreloading(false), []);

  useEffect(() => {
    if (!ready) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    if (anchor) setTimeout(() => scrollToSection(anchor), 250);
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: anchor ? `/${anchor}` : "/" }),
    }).catch(() => {});
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, [ready, anchor]);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.4 });

  return (
    <div className="relative min-h-screen bg-[#050505] text-zinc-100">
      {preloading && <Preloader onReady={onReady} onDone={onDone} />}
      {ready && (
        <>
          <div className="noise-overlay" aria-hidden="true" />
          <CursorGlow />
          <motion.div
            data-testid="scroll-progress-bar"
            style={{ scaleX }}
            className="fixed inset-x-0 top-0 z-[110] h-[2px] origin-left bg-[#E50914]"
          />
          <Navbar />
          <main>
            <Hero />
            <Marquee />
            <About />
            <Stack />
            <Services />
            <Process />
            <Portfolio />
            <Plans />
            <Testimonials />
            <Contact />
          </main>
          <Footer />
          <ChatWidget />
        </>
      )}
    </div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/planos" element={<Home anchor="planos" />} />
          <Route path="/entrar" element={<AuthPage />} />
          <Route path="/conta" element={<AccountPage />} />
          <Route path="/recuperar-senha" element={<ForgotPasswordPage />} />
          <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AnimatedRoutes />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: { background: "#101014", border: "1px solid #22222A", color: "#F4F4F6" },
          }}
        />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
